// src/tattoo/lib/textLayer.js
//
// Text layers for the type tool. A text layer is a normal image layer
// (so placement, 3D projection, and UV export all work unchanged) whose
// image is rendered from its text settings. Editing the text simply
// re-renders the image and swaps `layer.url`.
//
// Fonts are open-licence (SIL OFL / Apache) Google Fonts, loaded on demand.
// For commercial print work (e.g. the merch configurator) these can be
// self-hosted later without changing this module — only FONT_CSS_URL.

export const TEXT_FONTS = [
  { key: "script",     label: "Script",      family: "Great Vibes",          weight: 400, category: "Script" },
  { key: "pinyon",     label: "Elegant",     family: "Pinyon Script",        weight: 400, category: "Script" },
  { key: "blackletter",label: "Old English", family: "UnifrakturMaguntia",   weight: 400, category: "Blackletter" },
  { key: "typewriter", label: "Typewriter",  family: "Special Elite",        weight: 400, category: "Typewriter" },
  { key: "hand",       label: "Handwritten", family: "Caveat",               weight: 600, category: "Handwritten" },
  { key: "serif",      label: "Serif",       family: "Playfair Display",     weight: 700, category: "Serif" },
  { key: "block",      label: "Block",       family: "Bebas Neue",           weight: 400, category: "Sans" },
  { key: "varsity",    label: "Varsity",     family: "Graduate",             weight: 400, category: "Sports" },
  // sporty sans / jersey faces (MerchMod lists these first)
  { key: "jersey",     label: "Jersey",      family: "Teko",                 weight: 600, category: "Sports" },
  { key: "heavy",      label: "Heavy",       family: "Anton",                weight: 400, category: "Sports" },
  { key: "condensed",  label: "Condensed",   family: "Oswald",               weight: 700, category: "Sans" },
  { key: "sport",      label: "Sport",       family: "Russo One",            weight: 400, category: "Sports" },
  { key: "slab",       label: "Slab",        family: "Alfa Slab One",        weight: 400, category: "Sports" },
];

// MerchMod's order: sporty sans first, scripts last
export const SPORTS_FONT_ORDER = ["block", "varsity", "jersey", "heavy", "condensed", "sport", "slab", "serif", "typewriter", "hand", "blackletter", "script", "pinyon"];

const FONT_CSS_URL =
  "https://fonts.googleapis.com/css2?" +
  [
    "family=Great+Vibes",
    "family=Pinyon+Script",
    "family=UnifrakturMaguntia",
    "family=Special+Elite",
    "family=Caveat:wght@600",
    "family=Playfair+Display:wght@700",
    "family=Bebas+Neue",
    "family=Graduate",
    "family=Teko:wght@600",
    "family=Anton",
    "family=Oswald:wght@700",
    "family=Russo+One",
    "family=Alfa+Slab+One",
  ].join("&") +
  "&display=swap";

export const TEXT_DEFAULTS = {
  text: "Your text",
  fontKey: "script",
  color: "#111111",
  outlineColor: "#ffffff",
  outlineWidth: 0,      // px at the 200px reference size
  outlineAlign: "outside", // outside | center | inside (like Illustrator's Align Stroke)
  outlineJoin: "round",    // round | miter | bevel (corners)
  letterSpacing: 0,     // px at the 200px reference size
  lineHeight: 1.15,
  align: "center",      // "left" | "center" | "right"
  curve: 0,             // -100 (arc down / smile) … 0 (straight) … 100 (arc up / full circle)
};

export function fontForKey(key) {
  return TEXT_FONTS.find((f) => f.key === key) || TEXT_FONTS[0];
}

let cssInjected = false;
function injectFontCss() {
  if (cssInjected || typeof document === "undefined") return;
  cssInjected = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = FONT_CSS_URL;
  document.head.appendChild(link);
}

/** Make sure a font is actually loaded before drawing it to canvas.
 *  Canvas silently falls back to a default font if it isn't ready. */
export async function ensureFontLoaded(fontKey) {
  injectFontCss();
  const f = fontForKey(fontKey);
  if (typeof document === "undefined" || !document.fonts?.load) return;
  try {
    await document.fonts.load(`${f.weight} 64px "${f.family}"`);
  } catch (e) {
    console.warn("[textLayer] font load failed", f.family, e);
  }
}

/** Preload every font (used when the text dialog opens so the preview
 *  swatches render in their real faces). */
export async function preloadAllFonts() {
  await Promise.all(TEXT_FONTS.map((f) => ensureFontLoaded(f.key)));
}

// Reference font size the settings are expressed against. The final canvas
// is scaled so its long edge lands at MAX_EDGE for crisp, print-ready output.
const REF_SIZE = 200;
const MAX_EDGE = 2048;
// Room left for the outline is the SAME at every outline width (the
// slider's max), so thickening the outline never makes the image bigger,
// which would shrink the letters when the layer is fitted to its box.
const OUTLINE_ROOM = 24;

/** Corner style for outlines (Illustrator's Corner: miter / round / bevel). */
function setJoin(ctx, s) {
  const j = s.outlineJoin === "miter" || s.outlineJoin === "bevel" ? s.outlineJoin : "round";
  ctx.lineJoin = j;
  ctx.miterLimit = j === "miter" ? 8 : 2;
}

/** Fill + outline with Illustrator-style alignment. `draw(mode)` renders the
 *  glyphs with fillText ("fill") or strokeText ("stroke"). The outline's
 *  VISIBLE width is always `outline`. */
function paintOutlined(ctx, s, outline, draw) {
  const align = s.outlineAlign || "outside";
  if (outline > 0 && align === "outside") {
    ctx.strokeStyle = s.outlineColor;
    ctx.lineWidth = outline * 2; // centred stroke, half hidden under the fill
    draw("stroke");
  }
  ctx.fillStyle = s.color;
  draw("fill");
  if (outline > 0 && align !== "outside") {
    ctx.save();
    ctx.strokeStyle = s.outlineColor;
    if (align === "inside") {
      ctx.globalCompositeOperation = "source-atop"; // only on the letters
      ctx.lineWidth = outline * 2;
    } else {
      ctx.lineWidth = outline; // centre: half in, half out
    }
    draw("stroke");
    ctx.restore();
  }
}

function measureLine(ctx, line, spacing) {
  if (!line) return 0;
  const w = ctx.measureText(line).width;
  return w + Math.max(0, line.length - 1) * spacing;
}

function drawLine(ctx, line, x, y, spacing, mode) {
  if (!spacing) {
    if (mode === "stroke") ctx.strokeText(line, x, y);
    else ctx.fillText(line, x, y);
    return;
  }
  let cx = x;
  for (const ch of line) {
    if (mode === "stroke") ctx.strokeText(ch, cx, y);
    else ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
}

/**
 * Curved (arched) text. Each character is placed along a circle:
 *   curve > 0  → arches up like a rainbow (letters on the outside)
 *   curve < 0  → dips like a smile (letters on the inside)
 *   |curve| 100 → wraps a full 360° circle (e.g. puck / badge text)
 * Multiple lines are stacked as concentric arcs.
 * Note: characters are placed individually, so script fonts lose a
 * little of their joined-up flow at strong curves.
 */
function renderCurved(s, f, font, lines) {
  const m = document.createElement("canvas").getContext("2d");
  m.font = font;
  const spacing = Number(s.letterSpacing) || 0;
  const outline = Math.max(0, Number(s.outlineWidth) || 0);
  const sample = m.measureText("Hgjy|ÉÅ");
  const ascent = sample.actualBoundingBoxAscent || REF_SIZE * 0.8;
  const descent = Math.max(
    sample.actualBoundingBoxDescent || REF_SIZE * 0.25,
    REF_SIZE * 0.3
  );
  const lineStep = REF_SIZE * (Number(s.lineHeight) || 1.15);

  const curve = Math.max(-100, Math.min(100, Number(s.curve) || 0));
  const up = curve > 0;
  const theta = (Math.abs(curve) / 100) * Math.PI * 2; // total sweep of the longest line

  // per-line glyph advances
  const lineGlyphs = lines.map((ln) => {
    const chars = Array.from(ln || "");
    const widths = chars.map((ch) => m.measureText(ch).width);
    const total =
      widths.reduce((a, b) => a + b, 0) + Math.max(0, chars.length - 1) * spacing;
    return { chars, widths, total };
  });
  const longest = Math.max(1, ...lineGlyphs.map((g) => g.total));
  // Base radius from the longest line. A full circle leaves a small gap
  // so first and last letters don't collide.
  const R0 = longest / Math.max(theta * (Math.abs(curve) >= 100 ? 0.94 : 1), 0.01);

  // place glyphs around a centre at (0,0)
  const placed = []; // {ch, x, y, rot, w}
  lineGlyphs.forEach((g, i) => {
    // arch up: first line outermost; smile: first line innermost
    const R = Math.max(REF_SIZE * 0.5, up ? R0 - i * lineStep : R0 + i * lineStep);
    let sPos = -g.total / 2;
    g.chars.forEach((ch, k) => {
      const w = g.widths[k];
      const mid = sPos + w / 2;
      const a = mid / R; // arc angle offset from the top/bottom centre
      if (up) {
        const phi = -Math.PI / 2 + a;
        placed.push({ ch, w, x: R * Math.cos(phi), y: R * Math.sin(phi), rot: phi + Math.PI / 2 });
      } else {
        const phi = Math.PI / 2 - a;
        placed.push({ ch, w, x: R * Math.cos(phi), y: R * Math.sin(phi), rot: phi - Math.PI / 2 });
      }
      sPos += w + spacing;
    });
  });

  // bounds of every rotated glyph box (baseline at 0, -ascent … +descent)
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const extra = REF_SIZE * 0.2 + Math.max(outline, OUTLINE_ROOM); // script swash + outline allowance
  for (const p of placed) {
    const c = Math.cos(p.rot), sn = Math.sin(p.rot);
    const hw = p.w / 2 + extra;
    for (const [lx, ly] of [[-hw, -ascent - extra], [hw, -ascent - extra], [hw, descent + extra], [-hw, descent + extra]]) {
      const X = p.x + lx * c - ly * sn;
      const Y = p.y + lx * sn + ly * c;
      if (X < x0) x0 = X; if (Y < y0) y0 = Y;
      if (X > x1) x1 = X; if (Y > y1) y1 = Y;
    }
  }
  if (!placed.length) { x0 = y0 = 0; x1 = y1 = REF_SIZE; }

  const pad = Math.ceil(REF_SIZE * 0.15);
  const refW = Math.ceil(x1 - x0 + pad * 2);
  const refH = Math.ceil(y1 - y0 + pad * 2);
  const scale = MAX_EDGE / Math.max(refW, refH);
  const W = Math.max(1, Math.round(refW * scale));
  const H = Math.max(1, Math.round(refH * scale));

  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  ctx.scale(scale, scale);
  ctx.translate(pad - x0, pad - y0);
  ctx.font = font;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "center";
  setJoin(ctx, s);

  const pass = (mode) => {
    for (const p of placed) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      if (mode === "stroke") ctx.strokeText(p.ch, 0, 0);
      else ctx.fillText(p.ch, 0, 0);
      ctx.restore();
    }
  };
  // outside: all outlines first, then all fills, so outlines never cover
  // neighbours; centre / inside: over the fills (inside is clipped to them)
  paintOutlined(ctx, s, outline, pass);

  return { url: c.toDataURL("image/png"), width: W, height: H, refEdge: Math.max(refW, refH) };
}

/**
 * Render text settings to a transparent PNG data URL.
 * Returns { url, width, height }.
 */
export async function renderTextLayer(settings = {}) {
  const s = { ...TEXT_DEFAULTS, ...settings };
  const f = fontForKey(s.fontKey);
  await ensureFontLoaded(s.fontKey);

  const lines = String(s.text || " ").split(/\r?\n/);
  const font = `${f.weight} ${REF_SIZE}px "${f.family}", serif`;

  if (Math.abs(Number(s.curve) || 0) >= 1) {
    return renderCurved(s, f, font, lines);
  }

  // --- measure at reference size ---
  const m = document.createElement("canvas").getContext("2d");
  m.font = font;
  const spacing = Number(s.letterSpacing) || 0;
  const outline = Math.max(0, Number(s.outlineWidth) || 0);
  const lineWidths = lines.map((ln) => measureLine(m, ln, spacing));
  const sample = m.measureText("Hgjy|ÉÅ");
  const ascent = sample.actualBoundingBoxAscent || REF_SIZE * 0.8;
  const descent = sample.actualBoundingBoxDescent || REF_SIZE * 0.25;
  const lineStep = REF_SIZE * (Number(s.lineHeight) || 1.15);

  // Script faces have swashes that overhang their advance width — pad
  // generously so nothing is clipped.
  const pad = Math.ceil(REF_SIZE * 0.35 + Math.max(outline, OUTLINE_ROOM));
  const contentW = Math.max(1, ...lineWidths);
  // Script capitals (e.g. Great Vibes "Y") swash well below the measured
  // descent of ordinary glyphs — give the bottom extra room.
  const descentRoom = Math.max(descent * 1.5, REF_SIZE * 0.35);
  const contentH = ascent + descentRoom + lineStep * (lines.length - 1);
  const refW = Math.ceil(contentW + pad * 2);
  const refH = Math.ceil(contentH + pad * 2);

  // --- scale up to print resolution ---
  const scale = MAX_EDGE / Math.max(refW, refH);
  const W = Math.max(1, Math.round(refW * scale));
  const H = Math.max(1, Math.round(refH * scale));

  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  ctx.scale(scale, scale);
  ctx.font = font;
  ctx.textBaseline = "alphabetic";
  setJoin(ctx, s);

  const alignX = (w) =>
    s.align === "left" ? pad : s.align === "right" ? refW - pad - w : (refW - w) / 2;

  lines.forEach((ln, i) => {
    const y = pad + ascent + i * lineStep;
    const x = alignX(lineWidths[i]);
    paintOutlined(ctx, s, outline, (mode) => drawLine(ctx, ln, x, y, spacing, mode));
  });

  return { url: c.toDataURL("image/png"), width: W, height: H, refEdge: Math.max(refW, refH) };
}
